/**
 * JSON-LD builders for YADA pages.
 *
 * Each builder is a pure function returning a schema.org JSON object.
 * The `<SEO>` component serialises the result into a `<script type="application/ld+json">`
 * block — Google, Bing and other crawlers read structured data from there.
 *
 * Spec: https://schema.org/Organization / EducationalOrganization / Article / BreadcrumbList
 *        / WebSite / Person / FAQPage / Event / Course
 */

import type { Lecture } from '@/content/lectures'
import {
  LEGAL_NAME,
  LEGAL_NAME_EN,
  OG_IMAGES,
  SITE_NAME,
  SITE_URL,
  YADA_SAME_AS,
} from './siteMeta'

// ────────────────────────────────────────────────────────────────────────────
// Shared fragments: @id roots for the canonical entities that the rest of
// the site links to via `@id`. Crawlers use these to dedupe entities and to
// build a knowledge graph (Google Knowledge Panel relies on this).
// ────────────────────────────────────────────────────────────────────────────
const ORG_ID = `${SITE_URL}#organization`
const ORG_EDUCATIONAL_ID = `${SITE_URL}#organization-educational`
const WEBSITE_ID = `${SITE_URL}#website`
const FOUNDER_ID = `${SITE_URL}#founder`

// ────────────────────────────────────────────────────────────────────────────
// buildOrganization — emitted on every page via @id linking.
// Schema.org Organization with a sameAs list for the social profiles Google
// may surface in the knowledge panel. Founder links to the Person schema.
// ────────────────────────────────────────────────────────────────────────────

export interface OrganizationInput {
  /** Per-locale description; falls back to a static zh-Hant string. */
  description?: string
  /** Per-locale founder name override. */
  founderName?: string
  /** Per-locale founder job title override. */
  founderJobTitle?: string
}

/**
 * @param input Optional overrides. If omitted, the Organization is built with
 * the canonical Chinese description. Pages on the en locale should pass an
 * English description so the JSON-LD matches the visible copy.
 */
export function buildOrganization(input: OrganizationInput = {}) {
  const description =
    input.description ??
    '青年耀進發展協會 YADA — 兼教育傳媒，提供品格教育、AI 升學及就業顧問、AI 微學習三大方案。'

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: LEGAL_NAME,
    alternateName: ['YADA', SITE_NAME, LEGAL_NAME_EN],
    legalName: LEGAL_NAME_EN,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.png`,
    image: OG_IMAGES.map((img) => `${SITE_URL}${img.url}`),
    description,
    foundingDate: '2018',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'HK',
      addressLocality: 'Hong Kong',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'info@yada.hk',
        telephone: '+852-6887-6400',
        areaServed: ['HK', 'CN', 'MO'],
        availableLanguage: ['zh-Hant', 'zh-Hans', 'en'],
      },
    ],
    founder: { '@id': FOUNDER_ID },
    employee: { '@id': FOUNDER_ID },
    sameAs: YADA_SAME_AS,
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// buildEducationalOrganization — niche schema for the homepage/About.
// EducationalOrganization is a subtype of Organization, so we link via @id.
// ────────────────────────────────────────────────────────────────────────────

export function buildEducationalOrganization() {
  return {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    '@id': ORG_EDUCATIONAL_ID,
    name: SITE_NAME,
    alternateName: 'YADA',
    url: SITE_URL,
    parentOrganization: { '@id': ORG_ID },
    areaServed: [
      { '@type': 'Country', name: 'Hong Kong' },
      { '@type': 'AdministrativeArea', name: 'New Territories' },
      { '@type': 'AdministrativeArea', name: 'Kowloon' },
      { '@type': 'AdministrativeArea', name: 'Hong Kong Island' },
    ],
    audience: {
      '@type': 'EducationalAudience',
      audienceType: 'teachers, parents, students, school administrators',
    },
    knowsAbout: [
      '品格教育 Character Education',
      'AI 升學 AI University Admissions',
      '生涯規劃 Career Planning',
      '家長講座 Parent Lectures',
      '教育傳媒 Education Media',
      '青年發展 Youth Development',
      '正向教育 Positive Education',
      'AI 微學習 AI Micro-learning',
    ],
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// buildWebSite — emitted on the homepage. Drives the sitelinks search box
// (when Google chooses to show one) by declaring the internal search action.
// Currently we don't have a /search page, but declaring the target is harmless
// and future-proofs the schema for when we add one.
// ────────────────────────────────────────────────────────────────────────────

export function buildWebSite() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    alternateName: 'YADA',
    url: SITE_URL,
    inLanguage: ['zh-Hant', 'zh-Hans', 'en'],
    publisher: { '@id': ORG_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/articles?q={search_term_string}`,
      },
      // Google's required-input syntax
      'query-input': 'required name=search_term_string',
    },
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// buildPersonForFounder — Ken Ngai / 魏遠強. Emitted on /about and on the
// homepage so the founder's name and alternate names are first-class
// entities in Google's knowledge graph. `knowsAbout` declares the topical
// authority that backs his expertise claims.
// ────────────────────────────────────────────────────────────────────────────

export interface PersonInput {
  /** Per-locale display name (e.g. "Ken Ngai" in en). */
  name?: string
  /** Per-locale job title. */
  jobTitle?: string
  /** Per-locale bio / description. */
  description?: string
  /** LinkedIn / Twitter / official URLs. */
  sameAs?: string[]
}

export function buildPersonForFounder(input: PersonInput = {}) {
  const base = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': FOUNDER_ID,
    name: '魏遠強先生 (Ken Ngai)',
    alternateName: ['Ken Ngai', 'Wei Yuanqiang', '魏遠強', 'Wei Yuan Qiang'],
    givenName: 'Yuanqiang',
    familyName: 'Wei',
    jobTitle: '創辦人 / 行政總監 (Founder & Executive Director)',
    worksFor: { '@id': ORG_ID },
    affiliation: { '@id': ORG_ID },
    url: `${SITE_URL}/about`,
    knowsAbout: [
      '品格教育 Character Education',
      'AI 升學 AI University Admissions',
      '生涯規劃 Career Planning',
      '家長講座 Parent Lectures',
      '教育傳媒 Education Media',
      '青年發展 Youth Development',
      '正向教育 Positive Education',
      '香港中學教育 Hong Kong Secondary Education',
    ],
  }

  return {
    ...base,
    ...(input.name && { name: input.name }),
    ...(input.jobTitle && { jobTitle: input.jobTitle }),
    ...(input.description && { description: input.description }),
    ...(input.sameAs && input.sameAs.length > 0 && { sameAs: input.sameAs }),
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// buildArticle — generic NewsArticle / Article schema. Used by:
//   - /lectures/:slug  (with section: 'Lectures')
//   - /magazine/:slug  (with section: 'Press Coverage')
//   - /articles/:slug  (with section: 'Articles')
//
// The old lecture-specific signature has been generalised. Callers that
// previously did `buildArticle(lecture)` must now build an `ArticleInput`.
// ────────────────────────────────────────────────────────────────────────────

export interface ArticleInput {
  /** Path under SITE_URL — used to derive @id and mainEntityOfPage. */
  path: string
  /** Page H1 / headline shown in the SERP. */
  headline: string
  /** Optional sub-headline. */
  alternativeHeadline?: string
  /** ISO 8601 date. */
  datePublished: string
  /** Optional ISO 8601 date for last update. */
  dateModified?: string
  /** Author display name. */
  author: string
  /** Hero image path (under SITE_URL) or absolute URL. */
  image: string
  /** Optional thumbnail path/URL. */
  thumbnailUrl?: string
  /** "Lectures" | "Press Coverage" | "Articles" — drives articleSection. */
  section?: string
  /** Free-form tags. */
  keywords?: string[]
  /** Defaults to ['zh-Hant', 'zh-Hans', 'en']. */
  inLanguage?: string[]
  /** 'NewsArticle' for press coverage / lectures, 'Article' for evergreen. */
  type?: 'NewsArticle' | 'Article'
}

function absoluteUrl(maybePath: string): string {
  if (maybePath.startsWith('http://') || maybePath.startsWith('https://')) return maybePath
  return `${SITE_URL}${maybePath.startsWith('/') ? maybePath : `/${maybePath}`}`
}

export function buildArticle(input: ArticleInput) {
  return {
    '@context': 'https://schema.org',
    '@type': input.type ?? 'NewsArticle',
    '@id': `${absoluteUrl(input.path)}#article`,
    headline: input.headline,
    alternativeHeadline: input.alternativeHeadline,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    inLanguage: input.inLanguage ?? ['zh-Hant', 'zh-Hans', 'en'],
    author: { '@type': 'Person', name: input.author },
    publisher: { '@id': ORG_ID },
    image: absoluteUrl(input.image),
    thumbnailUrl: input.thumbnailUrl ? absoluteUrl(input.thumbnailUrl) : absoluteUrl(input.image),
    mainEntityOfPage: absoluteUrl(input.path),
    articleSection: input.section ?? 'Articles',
    keywords: (input.keywords ?? []).join(', '),
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// buildFAQPage — emitted on /products (FAQ section) and on /about (Q&A blocks).
// Drives Google FAQ rich results in SERPs.
// ────────────────────────────────────────────────────────────────────────────

export interface FAQItem {
  question: string
  answer: string
}

export function buildFAQPage(items: FAQItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// buildEvent — emitted on /lectures/:slug. Schema.org Event captures the
// speaker, venue, host and date so lecture appearances can surface in Google's
// event carousel.
// ────────────────────────────────────────────────────────────────────────────

export interface EventInput {
  /** Path under SITE_URL — used to derive @id. */
  path: string
  /** Lecture / event title. */
  name: string
  /** ISO 8601 start date. */
  startDate: string
  /** ISO 8601 end date (optional — defaults to startDate + 1h assumption). */
  endDate?: string
  /** Venue name. */
  locationName?: string
  /** Full address (if known). */
  locationAddress?: string
  /** Speaker name. */
  performer?: string
  /** Optional event-level description. */
  description?: string
  /** Hero image path/URL. */
  image?: string
  /** Defaults to ['zh-Hant', 'zh-Hans', 'en']. */
  inLanguage?: string[]
  /** Free-form tags. */
  keywords?: string[]
  /** Event status — defaults to 'EventScheduled'. */
  eventStatus?: 'EventScheduled' | 'EventCancelled' | 'EventPostponed' | 'EventRescheduled'
  /** Attendance mode — defaults to 'OfflineEventAttendanceMode'. */
  attendanceMode?: 'OfflineEventAttendanceMode' | 'OnlineEventAttendanceMode' | 'MixedEventAttendanceMode'
}

export function buildEvent(input: EventInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    '@id': `${absoluteUrl(input.path)}#event`,
    name: input.name,
    startDate: input.startDate,
    ...(input.endDate && { endDate: input.endDate }),
    eventStatus: `https://schema.org/${input.eventStatus ?? 'EventScheduled'}`,
    eventAttendanceMode: `https://schema.org/${input.attendanceMode ?? 'OfflineEventAttendanceMode'}`,
    ...(input.locationName && {
      location: {
        '@type': 'Place',
        name: input.locationName,
        ...(input.locationAddress && {
          address: { '@type': 'PostalAddress', streetAddress: input.locationAddress },
        }),
      },
    }),
    ...(input.performer && {
      performer: { '@type': 'Person', name: input.performer },
    }),
    organizer: { '@id': ORG_ID },
    ...(input.description && { description: input.description }),
    inLanguage: input.inLanguage ?? ['zh-Hant', 'zh-Hans', 'en'],
    ...(input.image && { image: absoluteUrl(input.image) }),
    ...(input.keywords && input.keywords.length > 0 && {
      keywords: input.keywords.join(', '),
    }),
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// buildCourse — emitted on product pages (/products/harbour,
// /products/values-incubator, /products). Schema.org Course captures the
// offering as a structured course so it can surface in Google's course
// carousel and in Bing's course search.
// ────────────────────────────────────────────────────────────────────────────

export interface CourseInput {
  /** Path under SITE_URL. */
  path: string
  /** Course / product name. */
  name: string
  /** 1-2 sentence description. */
  description: string
  /** "Secondary", "Primary", "Higher Education" — defaults to Secondary. */
  educationalLevel?: string
  /** Course provider display name. Defaults to SITE_NAME. */
  providerName?: string
  /** Defaults to ['zh-Hant', 'zh-Hans', 'en']. */
  inLanguage?: string[]
  /** Free-form tags. */
  keywords?: string[]
}

export function buildCourse(input: CourseInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    '@id': `${absoluteUrl(input.path)}#course`,
    name: input.name,
    description: input.description,
    provider: {
      '@type': 'Organization',
      name: input.providerName ?? SITE_NAME,
      '@id': ORG_ID,
    },
    educationalLevel: input.educationalLevel ?? 'Secondary',
    inLanguage: input.inLanguage ?? ['zh-Hant', 'zh-Hans', 'en'],
    ...(input.keywords && input.keywords.length > 0 && {
      keywords: input.keywords.join(', '),
    }),
    url: absoluteUrl(input.path),
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// buildProductWithOffers — a product page that publishes its pricing table.
//
// Used by the SmartCindy page. Its three pricing tiers (Trial / Individual
// / School) are the strongest structured signal on the page: Google reads
// `offers` to build price-comparison and product snippets.
//
// The tiers are NOT quoted as FAQPage questions. schema.org's FAQPage
// requires genuine Question/Answer pairs, and mislabelling a list of
// claims as questions risks an "invalid structured data" verdict from
// Google's Rich Results Test — worse than emitting no FAQ schema at all.
// The page's trust list is prose for the reader, not a Q&A, so it stays
// out of the structured data entirely.
// ────────────────────────────────────────────────────────────────────────────

export interface ProductOffer {
  /** Tier name as shown in the pricing table (e.g. "Individual"). */
  name: string
  /**
   * Price as a decimal string, or null for a tier priced on request
   * ("School" — 按需報價). A null price omits `price` and `priceCurrency`
   * rather than emitting a fake 0, which would be a false claim.
   */
  price?: string
  /** ISO 4217 currency code. */
  priceCurrency?: string
  /** What the tier is for — the table's "適合" column. */
  description?: string
  /** Availability URL — the page this offer is described on. */
  url?: string
}

export interface ProductWithOffersInput {
  /** Path under SITE_URL where the product lives. */
  path: string
  name: string
  description: string
  /** One entry per pricing tier, in the order the table lists them. */
  offers: ProductOffer[]
  /** Absolute or site-relative image URL for the product. */
  image?: string
  /** Brand shown as the product's brand / provider name. */
  brand?: string
  /** Free-form keywords. */
  keywords?: string[]
  /** Locales the product is available in. */
  inLanguage?: string[]
}

export function buildProductWithOffers(input: ProductWithOffersInput) {
  const url = absoluteUrl(input.path)
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${url}#product`,
    name: input.name,
    description: input.description,
    url,
    ...(input.image && { image: input.image }),
    ...(input.brand && {
      brand: { '@type': 'Brand', name: input.brand },
    }),
    ...(input.keywords && input.keywords.length > 0 && {
      keywords: input.keywords.join(', '),
    }),
    ...(input.inLanguage && { inLanguage: input.inLanguage }),
    offers: input.offers.map((offer) => ({
      '@type': 'Offer',
      name: offer.name,
      ...(offer.price && {
        price: offer.price,
        priceCurrency: offer.priceCurrency ?? 'HKD',
      }),
      ...(offer.description && { description: offer.description }),
      ...(offer.url && { url: offer.url }),
      availability: 'https://schema.org/InStock',
    })),
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// buildBreadcrumbList — emitted on every non-home page to drive the SERP
// breadcrumb trail (the small "Home › Products › …" line under search hits).
// ────────────────────────────────────────────────────────────────────────────

export interface BreadcrumbItem {
  name: string
  path: string
}

export function buildBreadcrumbList(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  } as const
}

// ────────────────────────────────────────────────────────────────────────────
// Convenience: build the homepage's full JSON-LD payload.
// Returns an array so the <SEO> component emits multiple <script> tags —
// each <script> is parsed independently by the structured-data validator.
// ────────────────────────────────────────────────────────────────────────────

export function buildHomePageJsonLd() {
  return [
    buildOrganization(),
    buildEducationalOrganization(),
    buildWebSite(),
    buildBreadcrumbList([{ name: SITE_NAME, path: '/' }]),
  ]
}

// ────────────────────────────────────────────────────────────────────────────
// Convenience: build the about page's full JSON-LD payload (org + founder
// + breadcrumb). Pages on the en locale should pass an English description
// so the JSON-LD matches the visible copy.
// ────────────────────────────────────────────────────────────────────────────

export interface AboutPageJsonLdInput {
  description?: string
  founderName?: string
  founderJobTitle?: string
}

export function buildAboutPageJsonLd(input: AboutPageJsonLdInput = {}) {
  return [
    buildOrganization({ description: input.description }),
    buildPersonForFounder({
      name: input.founderName,
      jobTitle: input.founderJobTitle,
    }),
    buildBreadcrumbList([
      { name: SITE_NAME, path: '/' },
      { name: '關於我們', path: '/about' },
    ]),
  ]
}

// ────────────────────────────────────────────────────────────────────────────
// Convenience: build a lecture detail page's full JSON-LD payload.
// `buildArticle` + `buildEvent` + breadcrumb in one call.
// ────────────────────────────────────────────────────────────────────────────

export interface LectureJsonLdInput {
  lecture: Lecture
  /** Headline shown in the SERP (already localised by the page). */
  headline: string
  /** Optional sub-headline. */
  alternativeHeadline?: string
  /** Author/speaker (already localised by the page). */
  author: string
  /** Already-localised event venue name. */
  locationName?: string
  /** Short event description. */
  description?: string
  /** Already-localised keywords. */
  keywords?: string[]
}

export function buildLecturePageJsonLd(input: LectureJsonLdInput) {
  const path = `/lectures/${input.lecture.slug}`
  return [
    buildArticle({
      path,
      headline: input.headline,
      alternativeHeadline: input.alternativeHeadline,
      datePublished: input.lecture.date,
      author: input.author,
      image: input.lecture.photo1,
      thumbnailUrl: input.lecture.photo1Thumb,
      section: '講座 Lectures',
      keywords: input.keywords ?? ['YADA', '講座', '教育', input.lecture.hostZh, input.lecture.seriesZh],
      type: 'NewsArticle',
    }),
    buildEvent({
      path,
      name: input.headline,
      startDate: input.lecture.date,
      locationName: input.locationName ?? input.lecture.venueZh,
      performer: input.author,
      description: input.description,
      image: input.lecture.photo1,
      keywords: input.keywords,
    }),
    buildBreadcrumbList([
      { name: SITE_NAME, path: '/' },
      { name: '講座分享', path: '/lectures' },
      { name: input.headline, path },
    ]),
  ]
}
